import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { STAGE_LABELS } from "@/lib/constants";
import TrackerTable from "@/components/TrackerTable";
import { Inbox, Play, ExternalLink } from "lucide-react";

const TABS = [
  { id: "not_started",      label: "Assigned — Not Started", icon: Inbox },
  { id: "in_progress",      label: "Active — In Progress",   icon: Play },
  { id: "pending_external", label: "Pending External",       icon: ExternalLink },
];

export default function ParalegalWorkspace() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("not_started");

  const load = async () => {
    try { const { data } = await api.get("/workspace/paralegal"); setData(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load workspace"); }
  };
  useEffect(() => { load(); }, []);

  const advance = async (id, target) => {
    try {
      await api.post(`/contracts/${id}/stage`, { stage: target });
      toast.success(`Moved to ${STAGE_LABELS[target] || target}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Stage change failed"); }
  };

  if (!data) return <div className="p-6 text-slate-500 text-sm">Loading…</div>;
  const rows = data[tab] || [];

  return (
    <div className="p-6" data-testid="paralegal-workspace-page">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Phase 5 · My Workspace</div>
        <h1 className="text-2xl font-bold text-slate-900">Contract Analyst Workspace</h1>
        <p className="text-sm text-slate-500 mt-1">
          Your work, split by phase. Quick-advance contracts to attorney review or vendor review right from the list.
        </p>
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-5">
        {TABS.map(t => {
          const Icon = t.icon;
          const count = data.counts?.[t.id] ?? 0;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`pw-tab-${t.id}`}
                    className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px ${
                      tab === t.id ? "border-[#005289] text-[#005289] font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"
                    }`}>
              <Icon className="w-4 h-4"/> {t.label}
              <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${tab === t.id ? "bg-[#005289] text-white" : "bg-slate-100 text-slate-600"}`}>{count}</span>
            </button>
          );
        })}
        {data.counts?.overdue > 0 && (
          <span className="ml-auto self-center text-xs text-red-600 font-bold">{data.counts.overdue} overdue in progress</span>
        )}
      </div>

      <div data-testid={`pw-panel-${tab}`}>
        <TrackerTable
          rows={rows}
          onChanged={load}
          testIdPrefix={`pw-${tab}`}
          emptyText="Nothing here."
          actionColumn={(c) => (
            <>
              {c.stage === "analyst_working" && (
                <button onClick={() => advance(c.id, "attorney_review")} data-testid={`advance-atty-${c.contract_number}`}
                        className="h-7 px-3 text-xs font-semibold bg-[#005289] text-white rounded hover:opacity-90">
                  → Attorney
                </button>
              )}
              {c.stage === "attorney_review" && (
                <button onClick={() => advance(c.id, "vendor_review")} data-testid={`advance-vendor-${c.contract_number}`}
                        className="h-7 px-3 text-xs font-semibold bg-[#005289] text-white rounded hover:opacity-90">
                  → Vendor
                </button>
              )}
              {c.stage === "unassigned" && (
                <span className="text-xs text-slate-400">Awaiting manager</span>
              )}
            </>
          )}
        />
      </div>
    </div>
  );
}
